// The folder analysis as a document: one section per file, problems first with
// the action that resolves each, then information. Opened from the folder
// plan's report button or a file's Findings count.

import { useEffect, useRef, type ReactNode } from 'react';
import type { FolderSheetRef } from '@/lib/api';
import {
  countFindings,
  describeSkipReason,
  pct,
  refLabel,
  type FileFindings,
  type FolderFinding,
} from '@/lib/folderFindings';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/Button';

/** What "Map with a context tag" hands the wizard: the column whose values
 *  repeat across rows, and the column that tells those rows apart. */
export interface MapContext {
  subject: string;
  column: string;
}

export interface FolderReportSection {
  relative: string;
  summary: string;
  findings: FileFindings;
}

/** The folder plan's own state and functions, handed to the report's actions. */
export interface FolderReportActions {
  /** Include state by the plan's include key: a file's name, or `refLabel` of a sheet. */
  isIncluded: (key: string) => boolean;
  setIncluded: (key: string, on: boolean) => void;
  canMap: (sheet: FolderSheetRef) => boolean;
  /** Hand the sheet to the Spreadsheet wizard; with `context`, its rows'
   *  thing and the column that tells repeated rows apart (81.22). */
  map: (sheet: FolderSheetRef, context?: MapContext) => void;
  /** Load one item again by its load key; a Replace file asks first. */
  retry: (key: string) => void;
  cancelRetry: () => void;
  confirmingRetry: { key: string; graphLabel: string } | null;
  loadRunning: boolean;
  analyzeAgain: () => void;
  analyzeAgainDisabled: boolean;
}

const BTN =
  'rounded border border-border px-2 py-0.5 text-xs text-tres-text-primary hover:border-tres-action focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tres-action disabled:pointer-events-none disabled:opacity-40';
const PRESSED = 'border-tres-action bg-tres-submenu-bg font-semibold text-tres-action';

function sheetPhrase(ref: FolderSheetRef): string {
  return ref.sheet === 'Sheet1' ? 'this table' : `sheet “${ref.sheet}”`;
}

function countLabel(f: FileFindings): string {
  const parts: string[] = [];
  if (f.problems.length > 0) parts.push(`${f.problems.length} to resolve`);
  if (f.information.length > 0) {
    parts.push(`${f.information.length} ${f.information.length === 1 ? 'note' : 'notes'}`);
  }
  return parts.join(', ');
}

function revealSection(container: HTMLElement | null, relative: string): boolean {
  if (!container) return false;
  for (const el of container.querySelectorAll<HTMLElement>('[data-report-file]')) {
    if (el.dataset.reportFile === relative) {
      el.scrollIntoView({ block: 'start' });
      el.querySelector<HTMLElement>('h3')?.focus({ preventScroll: true });
      return true;
    }
  }
  return false;
}

function Choices({
  label,
  options,
}: {
  label: string;
  options: Array<{ label: string; pressed: boolean; onSelect: () => void }>;
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex flex-wrap gap-1">
      {options.map((o) => (
        <button
          key={o.label}
          type="button"
          aria-pressed={o.pressed}
          onClick={o.onSelect}
          className={cn(BTN, o.pressed && PRESSED)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ActionRow({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2 pt-1">{children}</div>;
}

function Problem({
  title,
  tone = 'warning',
  children,
}: {
  title: string;
  tone?: 'warning' | 'error';
  children: ReactNode;
}) {
  return (
    <li
      className={cn(
        'rounded-md border px-3 py-2',
        tone === 'error'
          ? 'border-tres-error/40 bg-tres-error/5'
          : 'border-tres-warning/40 bg-tres-warning/5',
      )}
    >
      <p className="text-sm font-medium">{title}</p>
      <div className="mt-1 space-y-1 text-xs text-tres-text-secondary">{children}</div>
    </li>
  );
}

function Note({ title, children }: { title: string; children: ReactNode }) {
  return (
    <li className="border-l-2 border-border pl-3">
      <p className="text-xs font-medium">{title}</p>
      <div className="mt-0.5 space-y-1 text-xs text-tres-text-secondary">{children}</div>
    </li>
  );
}

function MapButton({
  sheet,
  label,
  actions,
  context,
}: {
  sheet: FolderSheetRef;
  label: string;
  actions: FolderReportActions;
  context?: MapContext;
}) {
  return (
    <button
      type="button"
      className={BTN}
      disabled={!actions.canMap(sheet)}
      onClick={() => actions.map(sheet, context)}
    >
      {label}
    </button>
  );
}

function AnalyzeAgainButton({ actions }: { actions: FolderReportActions }) {
  return (
    <button
      type="button"
      className={BTN}
      disabled={actions.analyzeAgainDisabled}
      onClick={actions.analyzeAgain}
    >
      Analyze again
    </button>
  );
}

function ProblemItem({
  finding,
  actions,
}: {
  finding: FolderFinding;
  actions: FolderReportActions;
}) {
  switch (finding.kind) {
    case 'unreadable': {
      const skipped = !actions.isIncluded(finding.relative);
      return (
        <Problem tone="error" title="TReS could not read this file">
          <p>The reader reported: {finding.why}</p>
          <p>Fix or re-save the file, then analyze the folder again. Or skip it.</p>
          <ActionRow>
            <Choices
              label="Skip this file"
              options={[
                {
                  label: 'Skip this file',
                  pressed: skipped,
                  onSelect: () => actions.setIncluded(finding.relative, skipped),
                },
              ]}
            />
            <AnalyzeAgainButton actions={actions} />
          </ActionRow>
        </Problem>
      );
    }
    case 'load-failed': {
      const includeKey = finding.sheet ? refLabel(finding.sheet) : finding.key;
      const skipped = !actions.isIncluded(includeKey);
      const what = finding.sheet ? 'sheet' : 'file';
      const confirming =
        actions.confirmingRetry?.key === finding.key ? actions.confirmingRetry : null;
      return (
        <Problem
          tone="error"
          title={finding.sheet ? `Loading ${sheetPhrase(finding.sheet)} failed` : 'Loading failed'}
        >
          <p>The load reported: {finding.message}</p>
          {confirming ? (
            <>
              <p className="font-medium text-tres-error">
                This file is set to Replace. Retrying clears everything in {confirming.graphLabel}{' '}
                first, including anything this folder already loaded there.
              </p>
              <ActionRow>
                <button
                  type="button"
                  className={cn(BTN, 'border-tres-error/60 font-semibold hover:border-tres-error')}
                  disabled={actions.loadRunning}
                  onClick={() => actions.retry(finding.key)}
                >
                  Retry with replace
                </button>
                <button type="button" className={BTN} onClick={actions.cancelRetry}>
                  Cancel
                </button>
              </ActionRow>
            </>
          ) : (
            <ActionRow>
              <button
                type="button"
                className={BTN}
                disabled={actions.loadRunning}
                onClick={() => actions.retry(finding.key)}
              >
                Retry this {what}
              </button>
              <Choices
                label={`Skip this ${what}`}
                options={[
                  {
                    label: 'Skip',
                    pressed: skipped,
                    onSelect: () => actions.setIncluded(includeKey, skipped),
                  },
                ]}
              />
            </ActionRow>
          )}
          {actions.loadRunning && <p>A load is running. Retry becomes available when it ends.</p>}
        </Problem>
      );
    }
    case 'redundant-file': {
      const skipped = !actions.isIncluded(finding.relative);
      return (
        <Problem
          title={
            finding.duplicateOf ? 'Same content as another file' : 'A newer version is in the folder'
          }
        >
          <p>
            {finding.duplicateOf
              ? `It is identical to ${finding.duplicateOf}, so it is skipped to keep the same data from loading twice.`
              : `${finding.supersededBy ?? 'Another file'} is a newer version of this file, with a higher version number and a later modification date, so this one is skipped.`}
          </p>
          {finding.fileKind === 'sheet' ? (
            <>
              <p>It stays out of the load. To load it anyway, map it in the Spreadsheet wizard.</p>
              <ActionRow>
                <MapButton
                  sheet={{ file: finding.relative, sheet: 'Sheet1' }}
                  label="Map anyway"
                  actions={actions}
                />
              </ActionRow>
            </>
          ) : (
            <ActionRow>
              <Choices
                label="Skip or load this file"
                options={[
                  {
                    label: 'Skip this file',
                    pressed: skipped,
                    onSelect: () => actions.setIncluded(finding.relative, false),
                  },
                  {
                    label: 'Load anyway',
                    pressed: !skipped,
                    onSelect: () => actions.setIncluded(finding.relative, true),
                  },
                ]}
              />
            </ActionRow>
          )}
        </Problem>
      );
    }
    case 'sheet-skip-proposed': {
      const key = refLabel(finding.sheet);
      const on = actions.isIncluded(key);
      return (
        <Problem title={`TReS proposes skipping ${sheetPhrase(finding.sheet)}`}>
          <p>{describeSkipReason(finding.reason)}</p>
          <ActionRow>
            <Choices
              label="Include or skip"
              options={[
                {
                  label: 'Include anyway',
                  pressed: on,
                  onSelect: () => actions.setIncluded(key, true),
                },
                {
                  label: 'Keep skipped',
                  pressed: !on,
                  onSelect: () => actions.setIncluded(key, false),
                },
              ]}
            />
            {on && <MapButton sheet={finding.sheet} label="Map" actions={actions} />}
          </ActionRow>
          {on && (
            <p>It has no proposed mapping, so it loads only after you map it in the Spreadsheet wizard.</p>
          )}
        </Problem>
      );
    }
    case 'event-grain': {
      const n = finding.repeatedSubjects;
      return (
        <Problem title={`Repeated rows in ${sheetPhrase(finding.sheet)}`}>
          <ul className="list-disc space-y-0.5 pl-4">
            <li>
              {`${n.toLocaleString()} ${n === 1 ? 'value' : 'values'} of “${finding.subjectColumn}” ${n === 1 ? 'appears' : 'appear'} on more than one row.`}
            </li>
            <li>Rows that share a value merge into one on import, unless each carries a context tag.</li>
            {finding.qualifierColumns.length > 0 && (
              <li>
                {`Columns that tell those rows apart: ${finding.qualifierColumns.map((q) => `“${q}”`).join(', ')}.`}
              </li>
            )}
          </ul>
          <ActionRow>
            {finding.qualifierColumns[0] ? (
              <MapButton
                sheet={finding.sheet}
                label={`Map with “${finding.qualifierColumns[0]}” as the context`}
                actions={actions}
                context={{ subject: finding.subjectColumn, column: finding.qualifierColumns[0] }}
              />
            ) : (
              <MapButton sheet={finding.sheet} label="Map" actions={actions} />
            )}
          </ActionRow>
          {finding.qualifierColumns[0] && (
            <p>
              {`The wizard opens with “${finding.subjectColumn}” and “${finding.qualifierColumns[0]}” suggested as things, and every value of “${finding.subjectColumn}” holding in its “${finding.qualifierColumns[0]}”. Accept the suggestions there.`}
            </p>
          )}
        </Problem>
      );
    }
    case 'missing-bridge': {
      const sameFile = finding.other.sheet.file === finding.here.sheet.file;
      return (
        <Problem
          title={
            sameFile ? 'Two groups of sheets do not connect' : `Not connected to ${finding.other.sheet.file}`
          }
        >
          <ul className="list-disc space-y-0.5 pl-4">
            <li>
              {`No column links rows keyed by “${finding.here.column}” (${refLabel(finding.here.sheet)}) to rows keyed by “${finding.other.column}” (${refLabel(finding.other.sheet)}).`}
            </li>
            <li>
              If they describe the same things, map a column that holds the other identifier, or add a
              file that pairs the two identifiers and analyze the folder again.
            </li>
          </ul>
          <ActionRow>
            <MapButton
              sheet={finding.here.sheet}
              label={
                sameFile ? `Map a join column in ${sheetPhrase(finding.here.sheet)}` : 'Map a join column'
              }
              actions={actions}
            />
            {sameFile && (
              <MapButton
                sheet={finding.other.sheet}
                label={`Map a join column in ${sheetPhrase(finding.other.sheet)}`}
                actions={actions}
              />
            )}
            <AnalyzeAgainButton actions={actions} />
          </ActionRow>
        </Problem>
      );
    }
    default:
      return null;
  }
}

function InfoItem({ finding }: { finding: FolderFinding }) {
  switch (finding.kind) {
    case 'document-index':
      return (
        <Note title="A document">
          <p>{finding.note}</p>
        </Note>
      );
    case 'not-importable':
      return (
        <Note title="Not an importable format">
          <p>It is left out of the load.</p>
        </Note>
      );
    case 'reader-notes':
      return (
        <Note title={`How TReS read ${sheetPhrase(finding.sheet)}`}>
          <ul className="list-disc space-y-0.5 pl-4">
            {finding.notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </Note>
      );
    case 'identity-join':
      return (
        <Note title={`Joins ${refLabel(finding.other.sheet)}`}>
          <p>
            {`“${finding.here.column}” (${refLabel(finding.here.sheet)}) matches “${finding.other.column}” (${refLabel(finding.other.sheet)}).`}
          </p>
          <p>
            {`${finding.overlap.toLocaleString()} shared values. ${pct(finding.coverage)} of one column’s values appear in the other.`}
          </p>
        </Note>
      );
    case 'sparse-column':
      return (
        <Note title={`Blanks in “${finding.column.column}”`}>
          <p>{`Filled on ${pct(finding.nonBlankRatio)} of rows in ${refLabel(finding.column.sheet)}.`}</p>
          {finding.applicabilityColumn && (
            <p>
              {`It is filled only where “${finding.applicabilityColumn}” is ${finding.appliesWhen.join(' or ')}, so the blanks are expected, not missing data.`}
            </p>
          )}
        </Note>
      );
    default:
      return null;
  }
}

export function FolderAnalysisReport({
  open,
  focusFile,
  sections,
  unsupported,
  subfolderCount,
  mapNeedsRepick,
  actions,
  onClose,
}: {
  open: boolean;
  /** The file to scroll to on open; null opens at the top. */
  focusFile: string | null;
  sections: FolderReportSection[];
  /** Files left out before staging because TReS does not import their format. */
  unsupported: string[];
  subfolderCount: number;
  /** The picked files are gone (the plan was restored after navigating away). */
  mapNeedsRepick: boolean;
  actions: FolderReportActions;
  onClose: () => void;
}) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Focus goes back to whatever opened the report.
  useEffect(() => {
    if (!open) return;
    const returnTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => returnTo?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const id = window.requestAnimationFrame(() => {
      if (focusFile === null || !revealSection(bodyRef.current, focusFile)) {
        closeRef.current?.focus();
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [open, focusFile]);

  if (!open) return null;

  const totals = countFindings(sections.map((s) => s.findings));
  const folderNotes = unsupported.length > 0 || subfolderCount > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="folder-report-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg bg-card text-tres-text-primary shadow-xl">
        <div className="flex items-start justify-between border-b border-border px-5 py-4">
          <div>
            <h2 id="folder-report-title" className="text-lg font-semibold">
              Analysis report
            </h2>
            <p className="mt-0.5 text-xs text-tres-text-secondary">
              {totals.problems === 0
                ? 'No problems to resolve.'
                : `${totals.problems} ${totals.problems === 1 ? 'problem' : 'problems'} to resolve.`}
              {totals.information > 0 &&
                ` ${totals.information} ${totals.information === 1 ? 'note' : 'notes'} for your information.`}
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'ml-4')}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div ref={bodyRef} className="space-y-6 overflow-y-auto px-5 py-4">
          {mapNeedsRepick && (
            <div className="space-y-1 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
              <p>
                Map opens the files you picked, and they are no longer at hand after leaving this page.
                To map a sheet, analyze the folder again.
              </p>
              <ActionRow>
                <AnalyzeAgainButton actions={actions} />
              </ActionRow>
            </div>
          )}

          {sections.length > 1 && (
            <nav
              aria-label="Files in this report"
              className="rounded-md border border-border bg-muted/30 px-3 py-2"
            >
              <p className="text-xs font-semibold">Files in this report</p>
              <ul className="mt-1 space-y-0.5 text-xs">
                {sections.map((s) => (
                  <li key={s.relative} className="flex flex-wrap gap-x-2">
                    <button
                      type="button"
                      onClick={() => revealSection(bodyRef.current, s.relative)}
                      className={cn(buttonVariants({ variant: 'link', size: 'md' }), 'break-words text-left')}
                    >
                      {s.relative}
                    </button>
                    <span className="text-tres-text-secondary">{countLabel(s.findings)}</span>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          {sections.map((s, i) => (
            <section
              key={s.relative}
              data-report-file={s.relative}
              aria-labelledby={`folder-report-file-${i}`}
            >
              <h3
                id={`folder-report-file-${i}`}
                tabIndex={-1}
                className="rounded break-words text-sm font-semibold outline-none focus-visible:ring-2 focus-visible:ring-tres-action"
              >
                {s.relative}
              </h3>
              {s.summary && <p className="text-xs text-tres-text-secondary">{s.summary}</p>}
              {s.findings.problems.length > 0 && (
                <>
                  <h4 className="mt-3 text-xs font-semibold">To resolve</h4>
                  <ul className="mt-1 space-y-2">
                    {s.findings.problems.map((f, j) => (
                      <ProblemItem key={j} finding={f} actions={actions} />
                    ))}
                  </ul>
                </>
              )}
              {s.findings.information.length > 0 && (
                <>
                  <h4 className="mt-3 text-xs font-semibold">For your information</h4>
                  <ul className="mt-1 space-y-2">
                    {s.findings.information.map((f, j) => (
                      <InfoItem key={j} finding={f} />
                    ))}
                  </ul>
                </>
              )}
            </section>
          ))}

          {folderNotes && (
            <section aria-labelledby="folder-report-folder">
              <h3 id="folder-report-folder" className="text-sm font-semibold">
                The folder as a whole
              </h3>
              <h4 className="mt-3 text-xs font-semibold">For your information</h4>
              <ul className="mt-1 space-y-2">
                {unsupported.length > 0 && (
                  <Note
                    title={`${unsupported.length} ${unsupported.length === 1 ? 'file' : 'files'} in formats TReS does not import`}
                  >
                    <p>They were left out of the analysis.</p>
                    <ul className="list-disc space-y-0.5 pl-4">
                      {unsupported.map((name) => (
                        <li key={name} className="break-words">
                          {name}
                        </li>
                      ))}
                    </ul>
                  </Note>
                )}
                {subfolderCount > 0 && (
                  <Note title="Subfolders left alone">
                    <p>
                      {`${subfolderCount} ${subfolderCount === 1 ? 'file sits' : 'files sit'} in subfolders. Only the folder’s own files are analyzed.`}
                    </p>
                  </Note>
                )}
              </ul>
            </section>
          )}

          {sections.length === 0 && !folderNotes && (
            <p className="text-sm">The analysis found nothing to report for this folder.</p>
          )}
        </div>
      </div>
    </div>
  );
}
